import { AppConfigService } from '@/services/app-config.service';
import { BridgeInputService } from '@/services/bridge/bridge-input.service';
import { BridgeService } from '@/services/bridge/bridge.service';
import { NgClass, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { FileUploadType } from './enum/FileUploadType';
import { FileUploadFlowStateEnum } from './enum/file-upload-flow-state-enum';
import { MsgTypeUploadEnum } from './enum/msgTypeUpload.enum';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  standalone: true,
  imports: [NgClass, MatIconModule, NgIf, MatButtonModule, MatProgressSpinnerModule, TranslateModule]
})
export class FileUploadComponent implements OnInit, OnChanges, OnDestroy {
  @Output() userWantsUploadEvent = new EventEmitter<any>(); // file
  @Output() hasSelectedFile = new EventEmitter<boolean>(); // file
  @Output() newFile = new EventEmitter<File | null>(); // file
  @Output() hasError = new EventEmitter<boolean>();
  @Input() backendResponse;
  // si true : bouton "uploader le fichier"
  // si false : il n'y aura pas de bouton pour uploader le fichier
  @Input() isStandAlone = true;
  @Input() fileType: FileUploadType = FileUploadType.ALL;
  @Input() isLoading: boolean | null = null;
  @Input() text = 'uploadFile.chooseFile';
  @Input() uploadText = 'uploadFile.uploadFile';
  @Input() maxSize: number | null = null;
  @Input() theme: 'default' | 'big' = 'default';
  @Input() width: number | null = null;
  @Input() height: number | null = null;
  @Input() hasStrictSize = false;

  showScan = false;
  scanSub = new Subscription();
  MSG_TYPE_ENUM = MsgTypeUploadEnum;

  loadingModel: boolean;
  msgType: MsgTypeUploadEnum | null;
  flowState: FileUploadFlowStateEnum;
  FLOW_STATE = FileUploadFlowStateEnum;
  file;
  accept = '*/*';
  depasse = false;
  badStrictSize = false;

  isDragEnter: boolean;
  isDragLeave: boolean;

  constructor(private appConfigService: AppConfigService, private bridgeInputService: BridgeInputService) {
    this.file = null;
    this.msgType = null;
    this.flowState = FileUploadFlowStateEnum.UNSELECTED;
    this.isDragEnter = false;
    this.isDragLeave = false;
    this.setDefaultLoading();
  }

  ngOnInit(): void {
    this.showScan = this.appConfigService.isMobileApp;
    this.scanSub = this.bridgeInputService.newScanDoc$.subscribe(async (encodedFile64: string) => {
      const base64Response = await fetch(encodedFile64);
      const blob = await base64Response.blob();
      const newFile: File = new File([blob], 'Document scanné');
      this.fileSelected(newFile);
    });
  }
  ngOnDestroy(): void {
    this.scanSub.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.backendResponse && changes.backendResponse.currentValue) {
      this.onUploadResponseHandler(this.backendResponse);
    }
    if (changes.fileType && changes.fileType.currentValue) {
      this.setAccept(this.fileType);
    }
    if (!this.isStandAlone && changes.isLoading && changes.isLoading.currentValue !== null) {
      this.setLoading(changes.isLoading.currentValue);
    }
  }

  setDefaultLoading(): void {
    const model = false;
    this.loadingModel = model;
  }

  // dragenter
  onDragEnter(event: any) {
    if (!this.isDragEnter) {
      this.isDragEnter = true;
      this.isDragLeave = false;
    }
  }

  // dragover
  onDragOver(event: any) {
    event.preventDefault();
  }

  // dragleave
  onDragLeave(event: any) {
    this.isDragEnter = false;
    this.isDragLeave = true;
  }

  // drop
  onDrop(event) {
    event.preventDefault();
    this.isDragEnter = false;
    const files = event.dataTransfer.files;
    this.newFileHandler(files);
  }

  onUploadResponseHandler(backendResponse): void {
    let status = '';
    status = backendResponse?.status?.toString();

    // is ok status like : 200, 201....
    if (status.startsWith('2')) {
      this.msgType = MsgTypeUploadEnum.SUCCESS;
      this.onRemoveFile(true);
    } else {
      this.msgType = MsgTypeUploadEnum.ERROR;
    }
    this.setLoading(false);
  }

  /* EVENTS */

  /**
   * On file selected
   */
  onChangeFileInput(event: Event) {
    this.newFileHandler((event.target as HTMLInputElement).files);
  }

  onClickBtn(): void {
    this.userWantsUploadEvent.next(this.file);
    this.setLoading(true);
  }

  setLoading(val: boolean): void {
    this.loadingModel = val;
  }

  onRemoveFile(keepMsg: boolean) {
    this.flowState = FileUploadFlowStateEnum.UNSELECTED;
    this.clearSelectedFile();
    if (!keepMsg) {
      this.msgType = null;
    }
    this.hasSelectedFile.next(false);
    this.newFile.next(null);
  }

  async newFileHandler(files): Promise<void> {
    const file = files[0];

    // clone the file for android os
    // because when a file is selected from google drive the upload failed with the error net::ERR_UPLOAD_FILE_CHANGED
    // which seems to mean that the file changed datetime has been updated between the selection and the upload start
    if (this.appConfigService.isOSAndroid) {
      const buffer = await file.arrayBuffer();
      this.fileSelected(new File([buffer], file.name, { type: file.type }));
    } else {
      this.fileSelected(file);
    }
  }

  async fileSelected(file: File) {
    this.file = file;
    this.flowState = this.FLOW_STATE.SELECTED;
    this.hasSelectedFile.next(true);
    this.newFile.next(file);
    if (this.maxSize && file) {
      const sizeMo = Math.ceil(file.size / 1000000);
      this.depasse = sizeMo > this.maxSize ? true : false;
    } else {
      this.depasse = false;
    }

    if (this.hasStrictSize) {
      await this.strictSizehandler();
    }
    this.hasError.next(this.depasse || this.badStrictSize);
  }

  clearSelectedFile(): void {
    this.file = null;
  }

  setAccept(fileType: FileUploadType): void {
    let accept = '*/*';
    switch (fileType) {
      case FileUploadType.ALL:
        accept = '*/*';
        break;
      case FileUploadType.IMAGES:
        accept = 'image/png, image/jpeg';
        break;
      case FileUploadType.PNG:
        accept = 'image/png';
        break;
      default:
        accept = '*/*';
        break;
    }

    this.accept = accept;
  }

  strictSizehandler(): Promise<boolean> {
    return new Promise(() => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const blob = new Blob([new Uint8Array(e.target.result)], { type: this.file.type });
        const img = new Image();
        img.onload = (f: any) => {
          const width = f.target.width;
          const height = f.target.height;

          // check strict size
          let badStrictSize = false;
          if (this.hasStrictSize) {
            if (this.width !== width || this.height !== height) {
              badStrictSize = true;
            }
          }
          this.badStrictSize = badStrictSize;
        };

        img.src = URL.createObjectURL(blob);
      };
      reader.readAsArrayBuffer(this.file);
    });
  }

  startScan() {
    BridgeService.launchScan();
  }
}
